/* eslint-disable max-lines */
import { AttributeMetaData, AttributeTitles, UniversalGridTranslationColumn } from '@customTypes';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { UsersIcon } from '@heroicons/react/24/outline';
import { CameraIcon } from '@heroicons/react/24/outline';

import { EMPTY_STRING, STEP, ZERO } from './common';
import { INTERVIEW_STATUS, PROJECT_ROLES } from './homepage';
import { QUESTION_TYPES } from './form';
import { EN_LANG_CODE } from './interview';

export enum PROJECT_TABS {
  PROJECT_SETUP,
  INTERVIEWS,
  ANALYSIS,
}

export enum PROJECT_SETUP_TABS {
  PROJECT_DETAILS,
  KEY_BUYING_FACTORS,
  COMPETITORS,
  SEGMENTATION,
  GENERAL_QUESTIONS,
  SURVEY_CONFIGURATION,
}

export const PROJECT_SETUP_KEYS = [
  'project.projectSetup.projectDetails.title',
  'project.projectSetup.keyBuyingFactors.title',
  'project.projectSetup.competitors.title',
  'project.projectSetup.segmentation.title',
  'project.projectSetup.generalQuestions.title',
  'project.projectSetup.surveyConfiguration.title',
];

export const PROJECT_SETUP_FORMS = [
  PROJECT_SETUP_TABS.PROJECT_DETAILS,
  PROJECT_SETUP_TABS.KEY_BUYING_FACTORS,
  PROJECT_SETUP_TABS.COMPETITORS,
  PROJECT_SETUP_TABS.SEGMENTATION,
  PROJECT_SETUP_TABS.GENERAL_QUESTIONS,
  PROJECT_SETUP_TABS.SURVEY_CONFIGURATION,
];

export const projectAttributesInfo = [
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.0', key: 'CvaInput.Home55' },
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.1', key: 'CvaInput.Home56' },
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.2', key: 'CvaInput.Home57' },
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.3', key: 'CvaInput.Home58' },
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.4', key: 'CvaInput.Home59' },
  { i18nKey: 'project.projectSetup.keyBuyingFactors.info.5', key: 'CvaInput.Home60' },
];

export const PROJECT_TYPE_TRANSLATION = {
  'project.projectSetup.projectDetails.title': 'CvaProj.Setup1',
  'project.projectSetup.keyBuyingFactors.title': 'CvaProjWizz.1',
  'project.projectSetup.competitors.title': 'CvaProjWizz.2',
  'project.projectSetup.segmentation.title': 'CvaProjWizz.3',
  'project.projectSetup.generalQuestions.title': 'CvaInput.Wizz51',
  'project.projectSetup.surveyConfiguration.title': 'CvaProjWizz.5',
};

export const projectAttributeNameLenght = 27;

export enum INTERVIEW_TITLE {
  interviewCompany = 'INTERVIEWEE_COMPANY',
  company = 'COMPANY',
  companyInterview = 'COMPANY_INTERVIEWEE',
  interview = 'INTERVIEWEE',
}

export enum COMPETITOR_CONFIG_OPTIONS {
  businessBlanks = 'THIS_BUSINESS',
  selected = 'FROM_COMPETITOR_LIST',
  all = 'ALL',
  blank = 'UNDEFINED',
}

export const INTERVIEW_TITLE_OPTIONS = [
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.interviewCompanyOption',
    key: 'CvaProj.IviewOpts.6',
    value: INTERVIEW_TITLE.interviewCompany,
  },
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.companyOption',
    key: 'CvaProj.IviewOpts.4',
    value: INTERVIEW_TITLE.company,
  },
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.companyInterviewOption',
    key: 'CvaProj.IviewOpts.5',
    value: INTERVIEW_TITLE.companyInterview,
  },
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.interviewOption',
    key: 'CvaProj.SetupInterviewTitle',
    value: INTERVIEW_TITLE.interview,
  },
];

export const COMPETITOR_OPTIONS = [
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.competitorBusinessBlanksOption',
    key: 'CvaProj.IviewOpts.1',
    value: COMPETITOR_CONFIG_OPTIONS.businessBlanks,
  },
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.competitorSelectedOption',
    key: 'CvaProj.IviewOpts.2',
    value: COMPETITOR_CONFIG_OPTIONS.selected,
  },
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.competitorAllOption',
    key: 'CvaProj.IviewOpts.3',
    value: COMPETITOR_CONFIG_OPTIONS.all,
  },
];

export enum PORT {
  SMTP = 465,
  SMTP2ND = 587,
  DEFAULT = '',
}

export const PORT_OPTIONS = [
  { label: 'SMTP — 465 (SSL/TLS)', value: PORT.SMTP },
  { label: 'SMTP — 587 (STARTTLS)', value: PORT.SMTP2ND },
];

export const surveyConfigurationDefaultValues = {
  titleConfig: INTERVIEW_TITLE.interviewCompany,
  introduction: EMPTY_STRING,
  instructions: EMPTY_STRING,
  competitorConfig: COMPETITOR_CONFIG_OPTIONS.businessBlanks,
  blankCompetitorCount: null,
  emailFooter: EMPTY_STRING,
  replyEmail: EMPTY_STRING,
  friendlyName: EMPTY_STRING,
  sendFrom: EMPTY_STRING,
  friendlyName2: EMPTY_STRING,
  password: EMPTY_STRING,
  emailServer: EMPTY_STRING,
  emailPort: PORT.DEFAULT,
};

export const surveyConfigurationInfo = [
  { i18nKey: 'project.projectSetup.surveyConfiguration.surveyConfigurationInfo.0', key: 'CvaProj.Setup66' },
  { i18nKey: 'project.projectSetup.surveyConfiguration.surveyConfigurationInfo.1', key: 'CvaProj.Setup67' },
  { i18nKey: 'project.projectSetup.surveyConfiguration.surveyConfigurationInfo.2', key: 'CvaProj.Setup68' },
  { i18nKey: 'project.projectSetup.surveyConfiguration.surveyConfigurationInfo.3', key: 'CvaProj.Setup69' },
];

export const surveyConfigurationTextFieldRows = 5;
export const PROJECT_YEAR_MIN = 1999;

export const TBUS_NAME_RANGE = {
  MIN: 2,
  MAX: 100,
};

export const PROJECT_SCOPE_RANGE = {
  MIN: 10,
  MAX: 500,
};

export const staffCreationDefaultValues = {
  countryCode: EMPTY_STRING,
  email: EMPTY_STRING,
  firstName: EMPTY_STRING,
  fluentLanguages: [{ code: String(ZERO) }],
  lastName: EMPTY_STRING,
  phone: EMPTY_STRING,
  projectRole: PROJECT_ROLES.UNDEFINED,
};

export const SEGMENTATION_CODE_LENGTH = {
  MIN: 1,
  MAX: 4,
};

export const SEGMENTATION_NAME_LENGTH = {
  MIN: 1,
  MAX: 50,
};

export const setupSegmentationInfo = [
  { i18nKey: 'project.projectSetup.segmentation.sectionTooltipInfo.0', key: 'CvaInput.Home162' },
  { i18nKey: 'project.projectSetup.segmentation.sectionTooltipInfo.1', key: 'CvaInput.Home73' },
  { i18nKey: 'project.projectSetup.segmentation.sectionTooltipInfo.2', key: 'CvaInput.Home74' },
  { i18nKey: 'project.projectSetup.segmentation.sectionTooltipInfo.3', key: 'CvaInput.Home75' },
];

export enum COMPETITOR_STATE {
  ASSIGN = 'assign',
  IGNORE = 'ignore',
  DEFAULT = '',
}

export const competitorDefaultValue = {
  shortId: EMPTY_STRING,
  businessName: EMPTY_STRING,
  state: COMPETITOR_STATE.DEFAULT,
  marketShare: null,
};

export const SHORT_ID_RANGE = {
  MIN: 1,
  MAX: 10,
};

export const BUSINESS_NAME_RANGE = {
  MIN: 2,
  MAX: 50,
};

export const QUESTION_TYPE_OPTIONS = [
  {
    i18nKey: 'project.projectSetup.generalQuestions.text',
    key: 'CvaInput.Home85',
    value: QUESTION_TYPES.OPEN,
  },
  { i18nKey: 'project.projectSetup.generalQuestions.numeric', key: 'CvaInput.Home86', value: QUESTION_TYPES.NUMERICAL },
  {
    i18nKey: 'project.projectSetup.generalQuestions.yesNo',
    key: 'CvaInput.Home87',
    value: QUESTION_TYPES.CLOSE,
  },
  {
    i18nKey: 'project.projectSetup.generalQuestions.multichoice',
    key: 'CvaInput.Home88',
    value: QUESTION_TYPES.MULTIPART,
  },
];

export const QUESTION_OPTION_RANGE = {
  MIN: 1,
  MAX: 100,
};

export const PROJECT_QUESTION_RANGE = {
  MIN: 10,
  MAX: 256,
};

export const PROJECT_CATEGORY_RANGE = {
  MIN: 3,
  MAX: 20,
};

export const COMPANY_NAME_RANGE = {
  MIN: 2,
  MAX: 30,
};

export const PROJECT_SETUP_COMPLETENESS_MAP = [
  'projectDetailsCompleted',
  'projectAttributesCompleted',
  'projectCompetitorsCompleted',
  'projectSegmentationCompleted',
  'projectQuestionsCompleted',
  'projectConfigurationCompleted',
];

export const PROJECT_SETUP_MANDATORY_COMPLETENESS = [
  PROJECT_SETUP_COMPLETENESS_MAP[0],
  PROJECT_SETUP_COMPLETENESS_MAP[1],
  PROJECT_SETUP_COMPLETENESS_MAP[2],
  PROJECT_SETUP_COMPLETENESS_MAP[5],
];

export const CREATE_PROJECT_BUTTON_WIDTH = 220;
export const ADD_INTERVIEW_BUTTON_WIDTH = 336;

export const selectInterviewFiltersDefaultValues = {
  country: ZERO,
  interviewerId: EMPTY_STRING,
  languageCode: EMPTY_STRING,
  onlyUncompleted: String(INTERVIEW_STATUS.ALL),
};

export const INTERVIEWEE_NAME_RANGE = {
  MIN: 2,
  MAX: 100,
};

export const APPOINTMENT_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

export const ADDRESS_MAX_LENGTH = 200;

export const defaultInterviewDetailsValues = {
  interviewType: EMPTY_STRING,
  company: EMPTY_STRING,
  interviewee: EMPTY_STRING,
  address: EMPTY_STRING,
  phone: EMPTY_STRING,
  email: EMPTY_STRING,
  jobTitle: EMPTY_STRING,
  postcode: EMPTY_STRING,
  mobileNumber: EMPTY_STRING,
  interviewerId: EMPTY_STRING,
  jointInterviewId: EMPTY_STRING,
  salesContact: EMPTY_STRING,
  langCode: String(EN_LANG_CODE),
  appointmentDate: EMPTY_STRING,
  segments: [],
  priority: ZERO,
  businessId: ZERO,
  worksheetId: ZERO,
};

export const EMAIL_TEXT_RANGE = {
  MIN: 1,
  MAX: 1000,
};

export enum PROJECT_ANALYZE_TABS {
  CHARTS,
  GENERAL_QUESTIONS_ANALYSIS,
  SCENARIOS,
  COMMENTS,
  RATING_DISTRIBUTION,
  INDIVIDUAL_RESULTS,
  VALUE_COMPARISON,
  ATTRIBUTE_ANALYSIS,
  TIMES_SERIES,
}

export const PROJECT_ANALYSIS_MAP = [
  {
    i18nKey: 'project.analysis.charts.title',
    key: 'CvaProj.Charts',
    value: PROJECT_ANALYZE_TABS.CHARTS,
  },
  {
    i18nKey: 'project.analysis.generalQuestionsAnalysis.title',
    key: 'Cvaproj.Setup74',
    value: PROJECT_ANALYZE_TABS.GENERAL_QUESTIONS_ANALYSIS,
  },
  {
    i18nKey: 'project.analysis.scenarios.title',
    key: 'CvaProj.Scenarios',
    value: PROJECT_ANALYZE_TABS.SCENARIOS,
  },
  {
    i18nKey: 'project.analysis.attributeDetails.title',
    key: 'CvaProj.Setup93',
    subtabs: [
      {
        i18nKey: 'project.analysis.attributeDetails.comments',
        key: 'CvaInput.Summary36',
        value: PROJECT_ANALYZE_TABS.COMMENTS,
      },
      {
        i18nKey: 'project.analysis.attributeDetails.ratingDistribution',
        key: 'CvaProj.SummaryPortal1',
        value: PROJECT_ANALYZE_TABS.RATING_DISTRIBUTION,
      },
    ],
  },
  {
    i18nKey: 'project.analysis.individualResults.title',
    key: 'CvaProj.Setup94',
    value: PROJECT_ANALYZE_TABS.INDIVIDUAL_RESULTS,
  },
  {
    i18nKey: 'project.analysis.compareAggregation.title',
    key: 'CvaProj.Setup95',
    subtabs: [
      {
        i18nKey: 'project.analysis.compareAggregation.valueComparison',
        key: 'CvaProj.Setup96',
        value: PROJECT_ANALYZE_TABS.VALUE_COMPARISON,
      },
      {
        i18nKey: 'project.analysis.compareAggregation.attributeAnalysis',
        key: 'CvaProj.SummaryPortal2',
        value: PROJECT_ANALYZE_TABS.ATTRIBUTE_ANALYSIS,
      },
    ],
  },
  {
    i18nKey: 'project.analysis.timeSeries.title',
    key: 'CvaProj.TSHeader',
    value: PROJECT_ANALYZE_TABS.TIMES_SERIES,
  },
];

export const PROJECT_ANALYZE_FORMS = [
  PROJECT_ANALYZE_TABS.CHARTS,
  PROJECT_ANALYZE_TABS.GENERAL_QUESTIONS_ANALYSIS,
  PROJECT_ANALYZE_TABS.SCENARIOS,
  PROJECT_ANALYZE_TABS.COMMENTS,
  PROJECT_ANALYZE_TABS.RATING_DISTRIBUTION,
  PROJECT_ANALYZE_TABS.INDIVIDUAL_RESULTS,
  PROJECT_ANALYZE_TABS.VALUE_COMPARISON,
  PROJECT_ANALYZE_TABS.ATTRIBUTE_ANALYSIS,
  PROJECT_ANALYZE_TABS.TIMES_SERIES,
];

export const defaultDataFilters = {
  benchmark: EMPTY_STRING,
  category: EMPTY_STRING,
  aggregation: EMPTY_STRING,
};

export const defaultGeneralQuestionsAnalysis = {
  question: EMPTY_STRING,
  analyseType: null,
};

export const defaultRatingDistributionAttribute = {
  attribute: EMPTY_STRING,
} as const;

export enum QUESTION_ANALYSE_TYPES {
  TOPIC_CLASSIFICATION = 'TOPIC_CLASSIFICATION',
  WORD_FREQUENCY = 'WORD_FREQUENCY',
  SENTIMENT_ANALYSIS = 'SENTIMENT_ANALYSIS',
  TEXT_SUMMARY = 'TEXT_SUMMARY',
}

export const QUESTION_ANALYSE_OPTIONS = [
  {
    i18nKey: 'project.analysis.generalQuestionsAnalysis.topicClassification',
    key: 'Cvaproj.Setup80',
    value: QUESTION_ANALYSE_TYPES.TOPIC_CLASSIFICATION,
  },
  {
    i18nKey: 'project.analysis.generalQuestionsAnalysis.wordCloudAnalysis',
    key: 'Cvaproj.Setup81',
    value: QUESTION_ANALYSE_TYPES.WORD_FREQUENCY,
  },
  {
    i18nKey: 'project.analysis.generalQuestionsAnalysis.sentimentAnalysis',
    key: 'Cvaproj.Setup82',
    value: QUESTION_ANALYSE_TYPES.SENTIMENT_ANALYSIS,
  },
  {
    i18nKey: 'project.analysis.generalQuestionsAnalysis.summariseText',
    key: 'Cvaproj.Setup83',
    value: QUESTION_ANALYSE_TYPES.TEXT_SUMMARY,
  },
];

export const generalQuestionDefaultCategory = {
  category: 'General category',
  categoryId: ZERO,
  categorySeq: ZERO,
  questions: [],
};

export const segmentationDefaultCategory = {
  type: STEP,
  description: 'Segments',
  codes: {},
};

export const pieChartColors = ['#2FC25BD9', '#EF5060D9'];
export const barChartColor = '#0079E8D9';
export const positiveNegativeChartColors = {
  positive: '#82CD19D9',
  negative: '#E73C4CD9',
};

export const wordCloudGridSize = 20;

export const WORD_CLOUD_SIZE_RANGE = {
  MIN: 15,
  MAX: 33,
};

export const SENARIOS_RANGES = {
  PRICES: { min: -50, max: 50, step: 1 },
  ATTRIBUTES: { min: -10, max: 10, step: 1 },
  PRICES_WEIGTH: { min: 0.1, max: 100, step: 0.1 },
};

export const ATTRIBUTE_METADATA: AttributeMetaData = {
  PRODUCT: {
    i18nTitleKey: 'project.analysis.scenarios.attributes.product.title',
    titleKey: 'setLang.ProdAtts',
    tooltipTexti18nkey: 'project.analysis.scenarios.attributes.product.tooltip',
    tooltipTextKey: 'CvaInput.Home56',
  },
  SERVICE: {
    i18nTitleKey: 'project.analysis.scenarios.attributes.service.title',
    titleKey: 'setLang.ServAtts',
    tooltipTexti18nkey: 'project.analysis.scenarios.attributes.service.tooltip',
    tooltipTextKey: 'CvaInput.Home57',
  },
  IMAGE: {
    i18nTitleKey: 'project.analysis.scenarios.attributes.image.title',
    titleKey: 'setLang.ImAtts',
    tooltipTexti18nkey: 'project.analysis.scenarios.attributes.image.tooltip',
    tooltipTextKey: 'CvaInput.Home58',
  },
} as const;

export const PRICE_COLUMNS: UniversalGridTranslationColumn[] = [
  { field: 'name', i18nKey: 'project.analysis.scenarios.columns.competitor', key: 'CvaProj.Sens3' },
  { field: 'originalValue', i18nKey: 'project.analysis.scenarios.columns.original', key: 'CvaProj.Sens1' },
  { field: 'currentValue', i18nKey: 'project.analysis.scenarios.columns.current', key: 'CvaProj.Sens2' },
  { field: 'change', i18nKey: 'project.analysis.scenarios.columns.change', key: 'CvaProj.Setup91' },
];

export const PRICE_WEIGHT_COLUMNS: UniversalGridTranslationColumn[] = [
  { field: 'name', i18nKey: 'project.analysis.scenarios.columns.priceWeight', key: 'CvaProj.Setup92' },
  { field: 'originalValue', i18nKey: 'project.analysis.scenarios.columns.original', key: 'CvaProj.Sens1' },
  { field: 'change', i18nKey: 'project.analysis.scenarios.columns.current', key: 'CvaProj.Sens2' },
];

export const ATTRIBUTE_COLUMNS: UniversalGridTranslationColumn[] = [
  { field: 'name', i18nKey: 'project.analysis.scenarios.columns.attribute', key: 'CvaProj.Sens4' },
  { field: 'originalValue', i18nKey: 'project.analysis.scenarios.columns.original', key: 'CvaProj.Sens1' },
  { field: 'currentValue', i18nKey: 'project.analysis.scenarios.columns.current', key: 'vaProj.Sens2' },
  { field: 'change', i18nKey: 'project.analysis.scenarios.columns.pointChange', key: 'CvaProj.Sens12' },
];

export enum ANALYSIS_SENARIOS_TYPE {
  PRICES = 'priceAdjustments',
  ATTRIBUTES = 'scoresAdjustments',
  PRICES_WEIGTH = 'priceImportance',
  ALL = 'all',
}

export const SCENARIOS_CHART_STYLES = {
  height: '540px',
};

export enum MANAGEMENT_ACTIONS {
  ADD = 'add',
  EDIT = 'edit',
  DELETE = 'delete',
}

export const ATTRIBUTE_TITLES: AttributeTitles = {
  PRODUCT: {
    i18nKey: 'project.analysis.scenarios.attributes.product.title',
    key: 'setLang.ProdAtts',
    icon: ArchiveBoxIcon,
  },
  SERVICE: {
    i18nKey: 'project.analysis.scenarios.attributes.service.title',
    key: 'setLang.ServAtts',
    icon: UsersIcon,
  },
  IMAGE: {
    i18nKey: 'project.analysis.scenarios.attributes.image.title',
    key: 'setLang.ImAtts',
    icon: CameraIcon,
  },
};

export enum ADD_AGGREGATION_TABS {
  INDUSTRY,
  CUSTOMER_GROUP,
  REGIONAL_SPLIT,
  INTERVIEWS_LIST,
}

export const AGGREGATION_NAME_RANGE = {
  MIN: 2,
  MAX: 30,
};

export const CATEGORY_NAME_RANGE = {
  MIN: 3,
  MAX: 40,
};

export const AGGREGATIONS_DIALOG_KEY = 'aggregationsDialog';

export enum SETUP_LANGUAGE_TABS {
  GENERAL,
  PROJECT,
}

export enum GENERAL_SETUP_LANGUAGE_OPTIONS {
  SYSTEM,
  INPUT,
  REPORT,
}

export enum PROJECT_SETUP_LANGUAGE_OPTIONS {
  SURVEY_CONFIG,
  ATTRIBUTE,
  SEGMENT,
  QUESTION,
}

export const GENERAL_SETUP_LANGUAGE_MAP = [
  {
    i18nKey: 'addLanguage.generalTranslations.system',
    key: 'CvaProj.system',
    value: GENERAL_SETUP_LANGUAGE_OPTIONS.SYSTEM,
  },
  {
    i18nKey: 'addLanguage.generalTranslations.input',
    key: 'Generic.Input',
    value: GENERAL_SETUP_LANGUAGE_OPTIONS.INPUT,
  },
  {
    i18nKey: 'addLanguage.generalTranslations.report',
    key: 'CvaProj.general',
    value: GENERAL_SETUP_LANGUAGE_OPTIONS.REPORT,
  },
];

export const PROJECT_SETUP_LANGUAGE_MAP = [
  {
    i18nKey: 'project.projectSetup.surveyConfiguration.title',
    key: 'CvaProj.MainScreen14',
    value: PROJECT_SETUP_LANGUAGE_OPTIONS.SURVEY_CONFIG,
  },
  {
    i18nKey: 'project.projectSetup.keyBuyingFactors.title',
    key: 'CvaProjWizz.1',
    value: PROJECT_SETUP_LANGUAGE_OPTIONS.ATTRIBUTE,
  },
  {
    i18nKey: 'project.projectSetup.segmentation.title',
    key: 'CvaProjWizz.3',
    value: PROJECT_SETUP_LANGUAGE_OPTIONS.SEGMENT,
  },
  {
    i18nKey: 'project.projectSetup.generalQuestions.title',
    key: 'CvaInput.Wizz51',
    value: PROJECT_SETUP_LANGUAGE_OPTIONS.QUESTION,
  },
];

export const translationDialogDefaultState = {
  isOpen: false,
  changedValues: {},
};

export const COMPETITOR_STATE_OPTIONS = [
  {
    i18nKey: 'project.projectSetup.competitors.assign',
    key: 'CvaInput.Home68',
    value: COMPETITOR_STATE.ASSIGN,
  },
  {
    i18nKey: 'project.projectSetup.competitors.ignore',
    key: 'CvaInput.Home69',
    value: COMPETITOR_STATE.IGNORE,
  },
];

export const PROJECT_ACCESS_ROLES = [PROJECT_ROLES.ADMIN, PROJECT_ROLES.ANALYST, PROJECT_ROLES.READ_ONLY];

export const USER_TIMEZONE = 'UTC';

export const INTERVIEWEE_TEMPLATE_FILENAME = 'Template for Interviewee Data.xlsx';

export const INTERVIEWEE_LITERAL = '<INTERVIEWEE>';
export const INTERVIEW_TEMPLATE_FILENAME = `Interview with ${INTERVIEWEE_LITERAL}.xlsx`;

export const TRANSLATION_MAX_LENGTH = 160;

export const initialScenariosTypeLoading = {
  isLoading: false,
};

export const initialScenariosLoadingState = {
  [ANALYSIS_SENARIOS_TYPE.PRICES]: initialScenariosTypeLoading,
  [ANALYSIS_SENARIOS_TYPE.ALL]: initialScenariosTypeLoading,
  [ANALYSIS_SENARIOS_TYPE.ATTRIBUTES]: initialScenariosTypeLoading,
  [ANALYSIS_SENARIOS_TYPE.PRICES_WEIGTH]: initialScenariosTypeLoading,
};
